import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getChannelById, type Channel } from '@/services/channelService';
import {
  getAutomationStatus,
  getAutomationIA,
  getChatbotEnabledPublic,
} from '@/services/automationService';
import { AutomationBitrixBot } from '@/components/channels/AutomationBitrixBot';
import LinearChatbotFlow from '@/components/automation/LinearChatbotFlow';
import AIChatbotSection from '@/components/automation/AIChatbotSection';
import { useTranslation } from 'react-i18next';
import { Loader2, Ban, ArrowLeft } from 'lucide-react';

export default function AutomationPage() {
  const { t } = useTranslation();
  const { channelId } = useParams();
  const navigate = useNavigate();
  const [channel, setChannel] = useState<Channel | null>(null);
  const [tab, setTab] = useState<'linear' | 'ai'>('linear');
  const [aiActive, setAiActive] = useState(false);
  const [linearActive, setLinearActive] = useState(false);
  const [chatbotEnabled, setChatbotEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    if (!channelId) return;
    getChatbotEnabledPublic(channelId)
      .then((res) => setChatbotEnabled(Boolean(res?.chatbotEnabled)))
      .catch(() => setChatbotEnabled(true));
  }, [channelId]);

  useEffect(() => {
    if (channelId) {
      getChannelById(Number(channelId)).then((ch) => ch && setChannel(ch));
  // Estado de automatizaciones
      getAutomationStatus(channelId).then((s) => {
        const status = s as unknown as { isActive?: boolean } | null;
        setLinearActive(Boolean(status?.isActive));
      });
      getAutomationIA(channelId).then((ia) => {
        const iaState = ia as unknown as { isActive?: boolean } | null;
        setAiActive(Boolean(iaState?.isActive));
      });
    }
  }, [channelId]);

  // Elegir pestaña por estado actual (IA tiene prioridad visual)
  useEffect(() => {
    if (aiActive) setTab('ai');
    else if (linearActive) setTab('linear');
  }, [aiActive, linearActive]);

  console.log('AutomationPage render', channel);

  if (chatbotEnabled === null) {
    return (
      <div className="flex h-[calc(100vh-100px)] items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-green-500" />
      </div>
    );
  }

  if (chatbotEnabled === false) {
    return (
      <div className="flex h-[calc(100vh-100px)] items-center justify-center">
        <div className="text-center max-w-md px-4">
          <Ban className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-700 mb-2">
            {t('chatbotGloballyDisabledTitle')}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            {t('chatbotGloballyDisabledMessage')}
          </p>
          <button
            onClick={() => navigate(`/channel/${channelId}`)}
            className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-green-500 text-white text-sm font-medium hover:bg-green-600"
          >
            <ArrowLeft className="h-4 w-4" /> {t('back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-100px)]">
      {/* Left sidebar remains inside LinearChatbotFlow; tabs & install live outside */}
      <div className="flex-1 relative">
        <div className="absolute left-2 right-2 z-20 flex items-center justify-between">
          <div className="flex gap-2 bg-white/70 backdrop-blur px-2 py-1 rounded-md border">
            <button
              className={`px-3 py-1 rounded ${tab === 'linear' ? 'bg-green-500 text-white' : 'bg-gray-100'}`}
              onClick={() => !aiActive && setTab('linear')}
              disabled={aiActive}
            >
              {t('linearChatbot')}
            </button>
            <button
              className={`px-3 py-1 rounded ${tab === 'ai' ? 'bg-green-500 text-white' : 'bg-gray-100'}`}
              onClick={() => !linearActive && setTab('ai')}
              disabled={linearActive}
            >
              {t('aiChatbot')}
            </button>
          </div>
          {channel && (
            <AutomationBitrixBot
              channelId={channel.id}
              openChannelsBX={channel.openChannelsBX || []}
              bitrixChatBot={channel.bitrixChatBot}
            />
          )}
        </div>
        <div className="h-full pt-12">
          {tab === 'linear' && channelId && (
            <LinearChatbotFlow
              channelId={channelId}
              onActiveChange={(active) => setLinearActive(active)}
            />
          )}
          {tab === 'ai' && (
            <AIChatbotSection
              channelId={channelId}
              onActiveChange={(active) => setAiActive(active)}
            />
          )}
        </div>
      </div>
    </div>
  );
}